import {
  CaretDownOutlined,
  CaretUpOutlined,
  StarOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Checkbox, Flex, Spin, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useMemo, useState } from 'react';

import { Link } from '@suntex.core/nav';
import {
  Database_Procs_TeamTopPlayers_Item,
  TeamId,
  UserId,
} from '@suntex.fim/api-client';
import { useAuth } from '@suntex.fim/auth/react';
import { LoginType, TeamTopPlayersType } from '@suntex.fim/enums';
import { getFlags } from '@suntex.fim/flags';
import { useFramework, useSoundDisabled } from '@suntex.fim/framework';
import { Button, Card, LevelSelect } from '@suntex.fim/ui';

import { usePlayer } from '../hooks';
import { isValidPlayerId } from '../utils';

import { PlayerProfile } from './PlayerProfile';
import { StickerCount } from './StickerCount';

const TOP_PLAYERS_OPTIONS = [
  { key: TeamTopPlayersType.AllTime, label: 'All Time' },
  { key: TeamTopPlayersType.Today, label: 'Today' },
  { key: TeamTopPlayersType.Yesterday, label: 'Yesterday' },
  { key: TeamTopPlayersType.Week, label: 'Week' },
  { key: TeamTopPlayersType.Month, label: 'Month' },
];

const MAX_SIDEBAR_ITEMS = 5;

export function PlayerSidebar({
  noneUserRoute,
}: {
  noneUserRoute?: (teamId: TeamId, userId: UserId) => string;
} = {}) {
  const { api } = useFramework();
  const { login } = useAuth();
  const loginType = login?.loginType;
  const loginId = login?.id;
  const isIndividual = loginType === LoginType.IndividualPlayer;
  const { playerId, info, isPreview, playerInfo } = usePlayer();
  const [day, setDay] = useState(TeamTopPlayersType.AllTime);
  const [isTopPlayersCollapsed, setIsTopPlayersCollapsed] = useState(false);
  const { badges: showBadges, quests: showQuests } = getFlags().player;

  const [soundDisabled, setSoundDisabled] = useSoundDisabled();

  const { data: quests = [], isLoading: isQuestLoading } = useQuery({
    queryKey: ['player', 'quests', playerId, showQuests, isPreview],
    queryFn: async () => {
      return (
        await api.get_Player_Quests({
          playerId,
          includeCompletedQuests: false,
        })
      ).questItems;
    },
    enabled: showQuests && !isPreview && isValidPlayerId(playerId),
  });

  const { data: badges = [], isLoading: isBadgeLoading } = useQuery({
    queryKey: ['player', 'badges', playerId, showBadges, isPreview],
    queryFn: async () => {
      return (await api.get_Player_Badges({ playerId })).badges;
    },
    enabled: showBadges && !isPreview && isValidPlayerId(playerId),
  });

  const { data: playerData, isLoading: isPlayerDataLoading } = useQuery({
    queryKey: [
      'player',
      'sidebar',
      playerId,
      day,
      info.team.id,
      loginType,
      loginId,
      isPreview,
    ],
    queryFn: async () => {
      return api.get_Player_Index({
        playerId,
        teamId: info.team.id,
        type: day,
        loginType: loginType!,
        loginId: loginId!,
      });
    },
    enabled:
      day !== TeamTopPlayersType.AllTime &&
      !isIndividual &&
      !isPreview &&
      isValidPlayerId(playerId) &&
      !!loginType &&
      !!loginId,
  });

  // TODO: Remove this workaround once the sidebar has its own dedicated query
  // that shares a cache key with the context. For now we reuse playerInfo from
  // PlayerContext for the default AllTime view to avoid a duplicate API call.
  const effectivePlayerData =
    day === TeamTopPlayersType.AllTime ? playerInfo : playerData;

  const topPlayersTableData = useMemo(
    () =>
      effectivePlayerData?.topPlayers?.map((player, index) => ({
        ...player,
        key: index,
      })) ?? [],
    [effectivePlayerData?.topPlayers],
  );

  const columns = [
    {
      title: 'Name',
      dataIndex: 'displayName',
      key: 'displayName',
      render: (
        displayName: string,
        record: Database_Procs_TeamTopPlayers_Item,
      ) =>
        displayName ? (
          displayName
        ) : noneUserRoute && record.userId ? (
          <Link to={noneUserRoute(info.team.id, record.userId)}>None</Link>
        ) : (
          <span>None</span>
        ),
    },
    {
      title: 'User ID',
      dataIndex: 'userId',
      key: 'userId',
    },
    {
      title: 'Stickers',
      dataIndex: 'stickers',
      key: 'stickers',
      align: 'right',
      render: (stickers: number) => <StickerCount count={stickers} day={day} />,
    },
  ] satisfies ColumnsType<Database_Procs_TeamTopPlayers_Item>;

  return (
    <Flex vertical gap={12} className="player-sidebar">
      <Card>
        <PlayerProfile />
      </Card>

      {showQuests && (
        <Card
          title={
            <Flex justify="space-between" align="center">
              <span>My Quests</span>
              <Link className="fw-bold text-primary" to="/player/my-quests">
                See all
              </Link>
            </Flex>
          }
        >
          {isQuestLoading ? (
            <Spin />
          ) : (
            <Flex vertical gap={8}>
              {quests.slice(0, MAX_SIDEBAR_ITEMS).map((quest) => (
                <Flex align="center" gap={8} key={quest.id}>
                  <StarOutlined />
                  <span>{quest.description}</span>
                </Flex>
              ))}
            </Flex>
          )}
        </Card>
      )}

      {showBadges && (
        <Card
          title={
            <Flex justify="space-between" align="center">
              <span>My Badges</span>
              <Link className="fw-bold text-primary" to={`/player/my-badges`}>
                See all
              </Link>
            </Flex>
          }
        >
          {isBadgeLoading ? (
            <Spin />
          ) : (
            <Flex vertical gap={8}>
              {badges.slice(0, MAX_SIDEBAR_ITEMS).map((badge) => (
                <Flex align="center" gap={10} key={badge.id}>
                  <TrophyOutlined style={{ margin: '10px 0' }} />
                  <span className="fw-bold">{badge.badgeName}</span>
                </Flex>
              ))}
            </Flex>
          )}
        </Card>
      )}

      {!isIndividual && (
        <Card
          title={
            <Flex justify="space-between" align="center">
              <span>Top Players</span>
              <Flex gap={8} align="center">
                <LevelSelect
                  value={day}
                  onChange={setDay}
                  options={TOP_PLAYERS_OPTIONS}
                />
                <Button
                  intent="icon"
                  size="small"
                  icon={
                    isTopPlayersCollapsed ? (
                      <CaretDownOutlined />
                    ) : (
                      <CaretUpOutlined />
                    )
                  }
                  onClick={() => setIsTopPlayersCollapsed((prev) => !prev)}
                  aria-label={
                    isTopPlayersCollapsed
                      ? 'Expand top players'
                      : 'Collapse top players'
                  }
                />
              </Flex>
            </Flex>
          }
          size="small"
        >
          {!isTopPlayersCollapsed &&
            (isPlayerDataLoading ? (
              <Spin />
            ) : (
              <Table
                className="top-players-table"
                dataSource={topPlayersTableData}
                columns={columns}
                pagination={false}
              />
            ))}
        </Card>
      )}

      <Card>
        <Checkbox
          checked={soundDisabled}
          onChange={(e) => setSoundDisabled(e.target.checked)}
        >
          Sound disabled
        </Checkbox>
      </Card>
    </Flex>
  );
}
